<template>
  <Section
    :id="id"
    :aria-label="t('rateShop.rateShop')"
    class="shipment-rate-shop"
  >
    <template #header="{ headerId, loading }">
      <ShipmentRateShopHeader
        :id="headerId"
        v-model:active-tab-key="activeTabKey"
        :shipment="shipment"
        :rate-shop="rateShop"
        :rate-shop-loading="rateShopLoading"
        :tabs="tabs"
        :tab-state-key="tabStateKey"
        :carrier-settings="carrierSettings"
        :cargo-insurance-status="cargoInsuranceStatus"
        :estimated-mileage="estimatedMileage"
        :waterfall-tender="waterfallTender"
        :service-type-filters="serviceTypeFilters"
        :has-configured-brokers="hasConfiguredBrokers"
        :loading="loading"
        :active-loadboards="activeLoadboardSources"
        :can-cancel-all-spot-market-bids="canCancelAllSpotMarketBids"
        :is-batch-cancelling-spot-markets="isBatchCancellingSpotMarkets"
        @add-spot-quote="onAddSpotQuoteClicked"
        @cancel-all-spot-market-bids="onBatchCancelAllSpotMarkets"
        @command="emit('command', $event)"
        @post-to-loadboard="onPostToLoadboardClicked"
        @remove-loadboard="onRemoveLoadboard"
        @request-spot-market-bid="emit('request-spot-market-bid')"
        @send-to-brokerage="onSendToBrokerage"
        @update-rates="emit('update-rates')"
        @waterfall-tender="emit('waterfall-tender')"
      />
    </template>

    <template #default>
      <ShipmentRateShopTabs
        :id="id"
        v-model:active-tab-key="activeTabKey"
        v-model:service-type-filters="serviceTypeFilters"
        :shipment="shipment"
        :rate-shop="rateShop"
        :rate-shop-loading="rateShopLoading"
        :tabs="tabs"
        :spot-markets="spotMarketsForRateShop"
        :load-boards="activeLoadboards"
        :financial-status="financialStatus"
        :waterfall-tender="waterfallTender"
        :freight-allowance="freightAllowance"
        :disabled="isDisabled"
        @command="emit('command', $event)"
        @edit-brokerage-quote="emit('edit-brokerage-quote', $event)"
        @edit-quote="onEditQuoteClicked"
        @edit-spot-quote="onEditQuoteClicked"
        @rate-selected="onRateSelected"
        @rate-shop-completed="emit('rate-shop-completed')"
        @remove-loadboard="onRemoveLoadboard"
        @send-to-brokerage="onSendToBrokerage"
        @spot-market-cancelled="emit('update-rates')"
        @spot-market-resent="emit('update-rates')"
        @update:freight-allowance="emit('update:freight-allowance', $event)"
      />

      <EditQuoteRedesignModal
        v-if="arApFeatureFlagEnabled"
        v-model="spotQuote.data"
        v-model:visible="spotQuote.isShowingModal"
        :view-uplift="viewUplift"
        :deletable="spotQuote.data?.id !== acceptedRateQuoteId"
        :equipment-type="shipment.equipmentType"
        @delete="emit('spot-quote-deleted', $event)"
        @save="onSpotQuoteSaved"
        @update:visible="onSpotQuoteModalClosed"
      />
      <EditQuoteModal
        v-else
        v-model="spotQuote.data"
        v-model:visible="spotQuote.isShowingModal"
        :view-uplift="viewUplift"
        :deletable="spotQuote.data?.id !== acceptedRateQuoteId"
        :equipment-type="shipment.equipmentType"
        @delete="emit('spot-quote-deleted', $event)"
        @save="onSpotQuoteSaved"
        @update:visible="onSpotQuoteModalClosed"
      />

      <RateSelectionReasonModal
        v-model:visible="lowestCostReasonModal.visible"
        :lowest-price-quote="lowestPriceQuote"
        :selected-quote="lowestCostReasonModal.selectedQuote"
        @confirm="onRateSelectionConfirmed"
      />

      <PostToLoadboardModal
        v-model:visible="postToLoadboardModalVisible"
        :equipment-type="shipment.equipmentType"
        :requested-mode="shipment.requestedMode"
        :loading="postToLoadboardResponse.loading"
        :active-loadboards="activeLoadboardSources"
        @confirm="onPostToLoadboard"
      />

      <PostToLoadboardResultsModal
        v-model:visible="postToLoadboardResponseModalVisible"
        :responses="postToLoadboardResponse.data"
      />
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import axios from 'axios';
import { useConfirm } from 'primevue/useconfirm';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useRygenTabs, useToast } from '@rygen/rygen-ui/composables';
import { ActionCommand, CarrierSetting, Loadable } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import {
  useHasConfiguredBrokers,
  useLoadboards,
  useLowestCostReasonCode,
  useRateShop,
} from '@/composables';
import {
  AcceptQuotePayload,
  CargoInsuranceStatus,
  FreightAllowance,
  Loadboard,
  LoadboardSource,
  PostToLoadboardCommand,
  PostToLoadboardResponse,
  RateQuote,
  RateSelectionReason,
  RateShop,
  type ServiceTypeFilters,
  Shipment,
  ShipmentAction,
  ShipmentFinancialStatus,
  ShipmentStatus,
  SpotMarket,
  WaterfallTender,
  WaterfallTenderStatus,
} from '@/model';
import { useSpotMarketStore } from '@/stores/SpotMarket';

import EditQuoteRedesignModal from '@/components/rateShop/editQuote/EditQuoteRedesignModal.vue';
import EditQuoteModal from '@/components/rateShop/EditQuoteModal.vue';
import LoadboardBidsTab from '@/components/rateShop/loadboard/LoadboardBidsTab.vue';
import PostToLoadboardModal from '@/components/rateShop/loadboard/PostToLoadboardModal.vue';
import PostToLoadboardResultsModal from '@/components/rateShop/loadboard/PostToLoadboardResultsModal.vue';
import RateSelectionReasonModal from '@/components/rateShop/RateSelectionReasonModal.vue';
import ShipmentRateShopAcceptedRateQuoteTab from '@/components/shipment/rateShop/ShipmentRateShopAcceptedRateQuoteTab.vue';
import ShipmentRateShopHeader, {
  type ShipmentRateShopTab,
  type TabKey,
} from '@/components/shipment/rateShop/ShipmentRateShopHeader.vue';
import ShipmentRateShopOtherRatesTab from '@/components/shipment/rateShop/ShipmentRateShopOtherRatesTab.vue';
import ShipmentRateShopRatesTab from '@/components/shipment/rateShop/ShipmentRateShopRatesTab.vue';
import ShipmentRateShopSpotMarketsTab from '@/components/shipment/rateShop/ShipmentRateShopSpotMarketsTab.vue';
import ShipmentRateShopTabs from '@/components/shipment/rateShop/ShipmentRateShopTabs.vue';
import ShipmentRateShopWaterfallTenderTab from '@/components/shipment/rateShop/ShipmentRateShopWaterfallTenderTab.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

interface SpotQuoteData extends Loadable<RateQuote> {
  editIndex: number;
  isShowingModal: boolean;
}

const newSpotQuoteData = (): SpotQuoteData => ({
  data: new RateQuote({ source: 'SPOT_QUOTE' }),
  editIndex: -1,
  isShowingModal: false,
  loading: false,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    default: () => ({ loading: false }),
  },
  carrierSettings: {
    type: CarrierSetting,
    default: () => new CarrierSetting(),
  },
  estimatedMileage: {
    type: Number,
    default: () => 0,
  },
  financialStatus: {
    type: String as PropType<ShipmentFinancialStatus | undefined>,
    default: () => undefined,
  },
  waterfallTender: {
    type: Object as PropType<WaterfallTender | undefined>,
    default: () => undefined,
  },
  rateShopLoading: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  viewUplift: {
    type: Boolean,
    default: () => false,
  },
  freightAllowance: {
    type: Object as PropType<FreightAllowance | undefined>,
    default: () => undefined,
  },
  cargoInsuranceStatus: {
    type: String as PropType<CargoInsuranceStatus | undefined>,
    default: () => undefined,
    required: false,
  },
});
const {
  id,
  shipment,
  rateShop,
  rateShopLoading,
  carrierSettings,
  estimatedMileage,
  disabled,
  financialStatus,
  viewUplift,
  waterfallTender,
  freightAllowance,
  cargoInsuranceStatus,
} = toRefs(props);

const emit = defineEmits<{
  (event: 'command', payload: ActionCommand<ShipmentAction>): void;
  (event: 'edit-brokerage-quote', rateQuote: RateQuote): void;
  (event: 'rate-quote-updated', rateQuote: RateQuote): void;
  (event: 'rate-selected', payload: AcceptQuotePayload): void;
  (event: 'rate-shop-completed'): void;
  (event: 'request-spot-market-bid'): void;
  (event: 'send-to-brokerage', payload: AcceptQuotePayload): void;
  (event: 'spot-quote-added', spotQuote: RateQuote): void;
  (event: 'spot-quote-deleted', spotQuote: RateQuote): void;
  (event: 'update-rates'): void;
  (
    event: 'update:freight-allowance',
    freightAllowance?: FreightAllowance,
  ): void;
  (event: 'waterfall-tender'): void;
}>();

const serviceTypeFilters = ref<ServiceTypeFilters>({});
const acceptedRateQuoteId = toRef(() => shipment.value.acceptedRateQuoteId);

const arApFeatureFlagEnabled = useFlag('ar-ap-split-feat-flag');
const loadboardsEnabled = useFlag('loadboard-feat-flag');

const { t } = useI18n();
const { isRateSelectionReasonRequired } = useLowestCostReasonCode();
const { acceptedRateQuote, lowestPriceQuote } = useRateShop(rateShop, {
  serviceTypeFilters,
  acceptedRateQuoteId,
});
const { loadSpotMarkets, spotMarkets } = useSpotMarketStore();
const confirm = useConfirm();
const toast = useToast();
const { removeLoadboardFromShipment } = useLoadboards();

const spotQuote: Ref<SpotQuoteData> = ref(newSpotQuoteData());
const postToLoadboardModalVisible = ref(false);
const postToLoadboardResponseModalVisible = ref(false);
const activeLoadboards: Ref<Loadable<Loadboard[]>> = ref({ loading: false });
const postToLoadboardResponse: Ref<Loadable<PostToLoadboardResponse[]>> = ref({
  loading: false,
});

const lowestCostReasonModal = ref({
  visible: false,
  selectedQuote: new RateQuote(),
  isBrokerage: false,
});
const isBatchCancellingSpotMarkets = ref(false);

const spotMarketsForRateShop = computed((): Loadable<SpotMarket[]> => {
  const rateShopId = rateShop.value.data?.id;
  const emptySpotMarket = { loading: false };

  return rateShopId
    ? (spotMarkets.value[rateShopId] ?? emptySpotMarket)
    : emptySpotMarket;
});

const tabs = computed((): ShipmentRateShopTab[] => [
  {
    key: 'RATES',
    header: t('rateShop.rates', 2),
    visible: !acceptedRateQuote.value && !waterfallTender?.value,
    showMileage: true,
    actionButton: 'RateShop',
    component: ShipmentRateShopRatesTab,
  },
  {
    key: 'SPOT_MARKETS',
    header: t('carrierPortal.spotMarket.spotMarkets', 2),
    visible:
      !acceptedRateQuote.value && !!spotMarketsForRateShop.value.data?.length,
    showMileage: true,
    actionButton: 'SpotMarket',
    component: ShipmentRateShopSpotMarketsTab,
  },
  {
    key: 'ACCEPTED_RATE_QUOTE',
    header:
      acceptedRateQuote.value?.source === 'BROKERAGE'
        ? t('brokerages.brokerages')
        : t('carriers.carriers'),
    visible:
      !!acceptedRateQuote.value &&
      (!waterfallTender?.value || waterfallTender.value.isCanceled()) &&
      !waterfallTender?.value?.isInProgress() &&
      waterfallTender?.value?.status !==
        WaterfallTenderStatus.COMPLETED_WITHOUT_ACCEPTED_RATE_QUOTE,
    showMileage: true,
    actionButton: 'Shipment',
    component: ShipmentRateShopAcceptedRateQuoteTab,
  },
  {
    key: 'WATERFALL_TENDER',
    header: t('waterfallTender.waterfallTender'),
    visible: !!waterfallTender?.value && !waterfallTender.value.isCanceled(),
    showMileage: false,
    actionButton: 'Shipment',
    component: ShipmentRateShopWaterfallTenderTab,
  },
  {
    key: 'OTHER_RATES',
    header: t('common.otherParam', { param: t('rateShop.rates', 2) }),
    visible:
      !!acceptedRateQuote.value ||
      waterfallTender?.value?.status ===
        WaterfallTenderStatus.COMPLETED_WITHOUT_ACCEPTED_RATE_QUOTE,
    showMileage: false,
    actionButton: 'NONE',
    component: ShipmentRateShopOtherRatesTab,
  },
  {
    key: 'LOADBOARDS',
    header: t('rateShop.loadboards', 2),
    visible: loadboardsEnabled.value && !!activeLoadboards.value.data?.length,
    showMileage: false,
    actionButton: 'NONE',
    component: LoadboardBidsTab,
  },
]);
const { activeTabKey, tabStateKey } = useRygenTabs<TabKey, ShipmentRateShopTab>(
  tabs,
);

const shouldPromptForRateSelectionReason = computed(() => {
  return (
    isRateSelectionReasonRequired.value &&
    lowestPriceQuote.value &&
    lowestCostReasonModal.value.selectedQuote &&
    lowestCostReasonModal.value.selectedQuote.totalPrice &&
    lowestCostReasonModal.value.selectedQuote.totalPrice >
      (lowestPriceQuote.value.totalPrice ?? 0)
  );
});

const hasConfiguredBrokers = useHasConfiguredBrokers();

const isDisabled = computed(() => {
  return disabled.value || spotQuote.value.loading;
});

const activeLoadboardSources = computed(
  () => activeLoadboards.value.data?.map((l) => l.source).filter(notNull) ?? [],
);

const canCancelAllSpotMarketBids = computed(() => {
  return (
    shipment.value.status === ShipmentStatus.PLANNING &&
    (spotMarketsForRateShop.value.data?.some((sm) => sm.isCancellable) ?? false)
  );
});

const showSpotMarketTab = () => {
  activeTabKey.value = 'SPOT_MARKETS';
};

const onBatchCancelAllSpotMarkets = () => {
  confirm.require({
    group: 'rygen-confirm-dialog',
    header: t('carrierPortal.spotMarket.cancelAllSpotMarkets'),
    message: t('carrierPortal.spotMarket.confirmCancelAllSpotMarkets'),
    accept: async () => {
      isBatchCancellingSpotMarkets.value = true;
      try {
        const shipmentId = shipment.value.id!;
        await api.shipmentSpotMarketBatch.cancelSpotMarkets(shipmentId);
        const rateShopId = rateShop.value.data?.id;
        if (rateShopId && shipmentId) {
          await loadSpotMarkets(rateShopId, {
            shipmentId,
            bypassCache: true,
          });
        }
      } catch (e) {
        toast.addError({
          detail: t('errors.cancelingParam', {
            param: t('carrierPortal.spotMarket.spotMarkets', 2),
          }),
          error: e,
        });
      } finally {
        isBatchCancellingSpotMarkets.value = false;
      }
    },
    acceptClass: 'p-button-sm p-button-danger',
    acceptIcon: 'pi pi-times',
    acceptProps: { iconPos: 'right' },
    acceptLabel: t('carrierPortal.spotMarket.cancelAllSpotMarkets'),
    rejectClass: 'p-button-sm p-button-text',
    rejectLabel: t('common.dontCancelParam', {
      param: t('carrierPortal.spotMarket.spotMarkets', 2),
    }),
  });
};

/**
 * Handle a quote being saved.
 *
 * `rateQuote` may be a spot quote or contract rate if a user has
 * `rateShop:editCarrierRate` permission
 */
const onSpotQuoteSaved = (rateQuote: RateQuote) => {
  if (!rateQuote.id) {
    emit('spot-quote-added', rateQuote);
  } else {
    emit('rate-quote-updated', rateQuote);
  }
};

const onAddSpotQuoteClicked = () => {
  spotQuote.value.data = new RateQuote({ source: 'SPOT_QUOTE' });
  spotQuote.value.isShowingModal = true;
};

/**
 * Show the spot quote modal for the clicked rateQuote.
 *
 * `rateQuote` may be a spot quote or contract rate if a user has
 * `rateShop:editCarrierRate` permission
 */
const onEditQuoteClicked = (rateQuote: RateQuote) => {
  spotQuote.value.data = new RateQuote(rateQuote);
  spotQuote.value.isShowingModal = true;
};

const onSpotQuoteModalClosed = (isVisible: boolean) => {
  if (!isVisible) {
    spotQuote.value = newSpotQuoteData();
  }
};

const onRateSelected = (rateQuote: RateQuote) => {
  lowestCostReasonModal.value.selectedQuote = rateQuote;
  if (shouldPromptForRateSelectionReason.value) {
    lowestCostReasonModal.value.visible = true;
  } else {
    emit('rate-selected', {
      rateQuote,
      rateSelectionReason: undefined,
    });
  }
};

const onSendToBrokerage = (rateQuote?: RateQuote) => {
  const quote = rateQuote ?? new RateQuote({ source: 'BROKERAGE' });
  lowestCostReasonModal.value.selectedQuote = quote;
  if (shouldPromptForRateSelectionReason.value) {
    lowestCostReasonModal.value.isBrokerage = true;
    lowestCostReasonModal.value.visible = true;
  } else {
    emit('send-to-brokerage', {
      rateQuote: quote,
      rateSelectionReason: undefined,
    });
  }
};

const onRateSelectionConfirmed = (payload: RateSelectionReason) => {
  if (!payload.rateSelectionReasonType) {
    return;
  }

  const rateSelectionReason = new RateSelectionReason(payload);
  const acceptQuotePayload = {
    rateQuote: lowestCostReasonModal.value.selectedQuote,
    rateSelectionReason,
  };

  if (lowestCostReasonModal.value.isBrokerage) {
    emit('send-to-brokerage', acceptQuotePayload);
  } else {
    emit('rate-selected', acceptQuotePayload);
  }
};

const loadLoadboards = async (shipmentId: number) => {
  //Because loadboards are removed async when shipments are created there is a brief span where a shipment
  //exists with an accepted rate quote and loadboards have not been removed yet
  if (acceptedRateQuote.value) {
    return;
  }
  try {
    activeLoadboards.value.loading = true;
    activeLoadboards.value.data =
      await api.shipmentLoadboard.getAll(shipmentId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    activeLoadboards.value.error = e;
    console.error('Error loading active loadboards', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('rateShop.loadboards', 2),
      }),
      error: e,
    });
  } finally {
    activeLoadboards.value.loading = false;
  }
};

const onRemoveLoadboard = async (loadboardSource: LoadboardSource) => {
  activeLoadboards.value.loading = true;
  const loadboard = activeLoadboards.value.data?.find(
    (l) => loadboardSource === l.source,
  );
  if (loadboard == null) {
    console.error('No active loadboard found for ' + loadboardSource);
    toast.addError({
      detail: t('errors.removingParam', {
        param: t('rateShop.loadboards', 1),
      }),
      error: undefined,
    });
    activeLoadboards.value.loading = false;
    return;
  }
  if (loadboard.id && shipment.value.id) {
    await removeLoadboardFromShipment(loadboard, shipment.value);
    await loadLoadboards(shipment.value.id);
  }
  activeLoadboards.value.loading = false;
};

const onPostToLoadboardClicked = () => {
  postToLoadboardModalVisible.value = true;
};

const onPostToLoadboard = async (command: PostToLoadboardCommand) => {
  const shipmentId = shipment.value.id;
  if (!shipmentId) {
    return;
  }

  try {
    postToLoadboardResponse.value.loading = true;
    command.entityId = shipmentId;
    postToLoadboardResponse.value.data = await api.shipmentLoadboard.post(
      shipmentId,
      command,
    );
    postToLoadboardModalVisible.value = false;
    postToLoadboardResponseModalVisible.value = true;
    await loadLoadboards(shipmentId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error posting to load board', e);
    toast.addError({
      detail: t('rateShop.errorPostingToLoadboard'),
      error: e,
    });
  } finally {
    postToLoadboardResponse.value.loading = false;
  }
};

watch(
  () => rateShop.value.data?.id,
  (newVal: number | undefined) => {
    const shipmentId = shipment.value.id;
    if (newVal && shipmentId) {
      loadSpotMarkets(newVal, {
        shipmentId,
        bypassCache: true,
      });
      if (loadboardsEnabled.value) {
        loadLoadboards(shipmentId);
      }
    }
  },
  { immediate: true },
);

// Backend removes posted loadboards async on accept so clear loadboards on the frontend
watch(acceptedRateQuoteId, (newId, oldId) => {
  if (newId && !oldId && loadboardsEnabled.value) {
    activeLoadboards.value.data = [];
  }
});

// Backend removes posted loadboards async on accept so clear loadboards on the frontend
watch(acceptedRateQuoteId, (newId, oldId) => {
  if (newId && !oldId && loadboardsEnabled.value) {
    activeLoadboards.value.data = [];
  }
});

defineExpose({ showSpotMarketTab });
</script>

<style>
.shipment-rate-shop {
  .section-header {
    --section-header-margin: 0 0 var(--sp-6);

    .routing-guide-link.border-right-1 {
      padding-right: var(--sp-3);
    }
  }

  .section-data {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);

    .shipment-rate-shop-tabs {
      .p-tabpanel-active {
        .shipment-rate-shop-tab {
          display: flex;
          flex-direction: column;
          gap: var(--gap-1);
        }
      }
    }
  }
}
</style>
